/**
 * ISO 27001:2022 Annex A Controls
 * Reference for security control implementation
 * Reference: https://www.iso.org/standard/27001
 */

export interface IsoControlCategory {
  id: string;
  name: string;
  nameNb: string; // Norwegian name
  description: string;
  descriptionNb: string;
}

export interface IsoControl {
  id: string;
  categoryId: string;
  name: string;
  nameNb: string;
  description: string;
  descriptionNb: string;
  controlType: 'preventive' | 'detective' | 'corrective';
  ciaImpact: ('confidentiality' | 'integrity' | 'availability')[];
}

/**
 * ISO 27001:2022 Control Categories (Annex A themes)
 */
export const ISO_CONTROL_CATEGORIES: IsoControlCategory[] = [
  {
    id: 'A.5',
    name: 'Organizational controls',
    nameNb: 'Organisasjonskontroller',
    description: 'Controls related to organizational policies and procedures',
    descriptionNb:
      'Kontroller relatert til organisatoriske retningslinjer og prosedyrer',
  },
  {
    id: 'A.6',
    name: 'People controls',
    nameNb: 'Personellkontroller',
    description: 'Controls related to human resources security',
    descriptionNb: 'Kontroller relatert til personellsikkerhet',
  },
  {
    id: 'A.7',
    name: 'Physical controls',
    nameNb: 'Fysiske kontroller',
    description: 'Controls related to physical and environmental security',
    descriptionNb: 'Kontroller relatert til fysisk og miljømessig sikkerhet',
  },
  {
    id: 'A.8',
    name: 'Technological controls',
    nameNb: 'Teknologiske kontroller',
    description: 'Controls related to technology and systems',
    descriptionNb: 'Kontroller relatert til teknologi og systemer',
  },
];

/**
 * ISO 27001:2022 Controls - Selected controls relevant to CMDB
 * This is a curated subset focused on technical and operational controls
 */
export const ISO_CONTROLS: IsoControl[] = [
  // A.5 - Organizational controls
  {
    id: 'A.5.1',
    categoryId: 'A.5',
    name: 'Policies for information security',
    nameNb: 'Retningslinjer for informasjonssikkerhet',
    description:
      'Information security policy and topic-specific policies shall be defined, approved by management, published, communicated to and acknowledged by relevant personnel and relevant interested parties.',
    descriptionNb:
      'Retningslinjer for informasjonssikkerhet skal defineres, godkjennes av ledelsen, publiseres, kommuniseres til og bekreftes av relevant personell.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },
  {
    id: 'A.5.2',
    categoryId: 'A.5',
    name: 'Information security roles and responsibilities',
    nameNb: 'Roller og ansvar for informasjonssikkerhet',
    description:
      'Information security roles and responsibilities shall be defined and allocated.',
    descriptionNb:
      'Roller og ansvar for informasjonssikkerhet skal defineres og tildeles.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },
  {
    id: 'A.5.7',
    categoryId: 'A.5',
    name: 'Threat intelligence',
    nameNb: 'Trusselinformasjon',
    description:
      'Information relating to information security threats shall be collected and analysed to produce threat intelligence.',
    descriptionNb:
      'Informasjon om trusler mot informasjonssikkerheten skal samles inn og analyseres.',
    controlType: 'detective',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },
  {
    id: 'A.5.15',
    categoryId: 'A.5',
    name: 'Access control',
    nameNb: 'Tilgangskontroll',
    description:
      'Rules to control physical and logical access to information and other associated assets shall be established and implemented.',
    descriptionNb:
      'Regler for å kontrollere fysisk og logisk tilgang til informasjon og andre tilknyttede eiendeler skal etableres og implementeres.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity'],
  },
  {
    id: 'A.5.16',
    categoryId: 'A.5',
    name: 'Identity management',
    nameNb: 'Identitetsstyring',
    description: 'The full life cycle of identities shall be managed.',
    descriptionNb: 'Hele livssyklusen til identiteter skal styres.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity'],
  },
  {
    id: 'A.5.17',
    categoryId: 'A.5',
    name: 'Authentication information',
    nameNb: 'Autentiseringsinformasjon',
    description:
      'Allocation and management of authentication information shall be controlled.',
    descriptionNb:
      'Tildeling og administrasjon av autentiseringsinformasjon skal kontrolleres.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity'],
  },
  {
    id: 'A.5.18',
    categoryId: 'A.5',
    name: 'Access rights',
    nameNb: 'Tilgangsrettigheter',
    description:
      'Access rights to information and other associated assets shall be provisioned, reviewed, modified and removed.',
    descriptionNb:
      'Tilgangsrettigheter til informasjon og andre tilknyttede eiendeler skal tildeles, gjennomgås, endres og fjernes.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity'],
  },
  {
    id: 'A.5.23',
    categoryId: 'A.5',
    name: 'Information security for use of cloud services',
    nameNb: 'Informasjonssikkerhet ved bruk av skytjenester',
    description:
      'Processes for acquisition, use, management and exit from cloud services shall be established.',
    descriptionNb:
      'Prosesser for anskaffelse, bruk, administrasjon og avslutning av skytjenester skal etableres.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },
  {
    id: 'A.5.29',
    categoryId: 'A.5',
    name: 'Information security during disruption',
    nameNb: 'Informasjonssikkerhet under avbrudd',
    description:
      'The organization shall plan how to maintain information security at an appropriate level during disruption.',
    descriptionNb:
      'Organisasjonen skal planlegge hvordan man opprettholder informasjonssikkerhet under avbrudd.',
    controlType: 'preventive',
    ciaImpact: ['availability'],
  },
  {
    id: 'A.5.30',
    categoryId: 'A.5',
    name: 'ICT readiness for business continuity',
    nameNb: 'IKT-beredskap for forretningskontinuitet',
    description:
      'ICT readiness shall be planned, implemented, maintained and tested based on business continuity objectives and ICT continuity requirements.',
    descriptionNb:
      'IKT-beredskap skal planlegges, implementeres, vedlikeholdes og testes basert på mål for forretningskontinuitet og IKT-kontinuitetskrav.',
    controlType: 'preventive',
    ciaImpact: ['availability'],
  },

  // A.6 - People controls
  {
    id: 'A.6.3',
    categoryId: 'A.6',
    name: 'Information security awareness, education and training',
    nameNb: 'Bevissthet, opplæring og trening i informasjonssikkerhet',
    description:
      'Personnel shall receive appropriate awareness, education and training.',
    descriptionNb:
      'Personell skal motta passende bevisstgjøring, opplæring og trening.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },

  // A.7 - Physical controls
  {
    id: 'A.7.4',
    categoryId: 'A.7',
    name: 'Physical security monitoring',
    nameNb: 'Fysisk sikkerhetsovervåking',
    description:
      'Premises shall be continuously monitored for unauthorized access.',
    descriptionNb:
      'Lokaler skal overvåkes kontinuerlig for uautorisert tilgang.',
    controlType: 'detective',
    ciaImpact: ['confidentiality', 'integrity'],
  },

  // A.8 - Technological controls
  {
    id: 'A.8.1',
    categoryId: 'A.8',
    name: 'User endpoint devices',
    nameNb: 'Brukerens endepunktsenheter',
    description:
      'Information stored on, processed by or accessible via user endpoint devices shall be protected.',
    descriptionNb:
      'Informasjon som lagres på, behandles av eller er tilgjengelig via brukerens endepunktsenheter skal beskyttes.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity'],
  },
  {
    id: 'A.8.2',
    categoryId: 'A.8',
    name: 'Privileged access rights',
    nameNb: 'Privilegerte tilgangsrettigheter',
    description:
      'The allocation and use of privileged access rights shall be restricted and managed.',
    descriptionNb:
      'Tildeling og bruk av privilegerte tilgangsrettigheter skal begrenses og administreres.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },
  {
    id: 'A.8.3',
    categoryId: 'A.8',
    name: 'Information access restriction',
    nameNb: 'Informasjonstilgangsbegrensning',
    description:
      'Access to information and other associated assets shall be restricted.',
    descriptionNb:
      'Tilgang til informasjon og andre tilknyttede eiendeler skal begrenses.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality'],
  },
  {
    id: 'A.8.4',
    categoryId: 'A.8',
    name: 'Access to source code',
    nameNb: 'Tilgang til kildekode',
    description:
      'Read and write access to source code, development tools and software libraries shall be appropriately managed.',
    descriptionNb:
      'Lese- og skrivetilgang til kildekode, utviklingsverktøy og programvarebiblioteker skal administreres.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity'],
  },
  {
    id: 'A.8.5',
    categoryId: 'A.8',
    name: 'Secure authentication',
    nameNb: 'Sikker autentisering',
    description:
      'Secure authentication technologies and procedures shall be implemented.',
    descriptionNb:
      'Sikre autentiseringsteknologier og prosedyrer skal implementeres.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity'],
  },
  {
    id: 'A.8.6',
    categoryId: 'A.8',
    name: 'Capacity management',
    nameNb: 'Kapasitetsstyring',
    description:
      'The use of resources shall be monitored and adjusted in line with current and expected capacity requirements.',
    descriptionNb:
      'Bruk av ressurser skal overvåkes og justeres i henhold til nåværende og forventede kapasitetskrav.',
    controlType: 'preventive',
    ciaImpact: ['availability'],
  },
  {
    id: 'A.8.7',
    categoryId: 'A.8',
    name: 'Protection against malware',
    nameNb: 'Beskyttelse mot skadevare',
    description:
      'Protection against malware shall be implemented and supported by appropriate user awareness.',
    descriptionNb:
      'Beskyttelse mot skadevare skal implementeres og støttes av passende brukerbevissthet.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },
  {
    id: 'A.8.8',
    categoryId: 'A.8',
    name: 'Management of technical vulnerabilities',
    nameNb: 'Håndtering av tekniske sårbarheter',
    description:
      'Information about technical vulnerabilities shall be obtained, exposure evaluated and appropriate measures taken.',
    descriptionNb:
      'Informasjon om tekniske sårbarheter skal innhentes, eksponering evalueres og passende tiltak iverksettes.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },
  {
    id: 'A.8.9',
    categoryId: 'A.8',
    name: 'Configuration management',
    nameNb: 'Konfigurasjonsstyring',
    description:
      'Configurations, including security configurations, shall be established, documented, implemented, monitored and reviewed.',
    descriptionNb:
      'Konfigurasjoner, inkludert sikkerhetskonfigurasjoner, skal etableres, dokumenteres, implementeres, overvåkes og gjennomgås.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },
  {
    id: 'A.8.10',
    categoryId: 'A.8',
    name: 'Information deletion',
    nameNb: 'Informasjonssletting',
    description:
      'Information stored in information systems, devices or any other storage media shall be deleted when no longer required.',
    descriptionNb:
      'Informasjon lagret i informasjonssystemer, enheter eller andre lagringsmedier skal slettes når den ikke lenger er nødvendig.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality'],
  },
  {
    id: 'A.8.11',
    categoryId: 'A.8',
    name: 'Data masking',
    nameNb: 'Datamaskering',
    description:
      "Data masking shall be used in accordance with the organization's topic-specific policy.",
    descriptionNb:
      'Datamaskering skal brukes i henhold til organisasjonens retningslinjer.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality'],
  },
  {
    id: 'A.8.12',
    categoryId: 'A.8',
    name: 'Data leakage prevention',
    nameNb: 'Forebygging av datalekkasje',
    description:
      'Data leakage prevention measures shall be applied to systems, networks and any other devices.',
    descriptionNb:
      'Tiltak for å forebygge datalekkasje skal anvendes på systemer, nettverk og andre enheter.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality'],
  },
  {
    id: 'A.8.13',
    categoryId: 'A.8',
    name: 'Information backup',
    nameNb: 'Informasjonssikkerhetskopiering',
    description:
      'Backup copies of information, software and systems shall be maintained and regularly tested.',
    descriptionNb:
      'Sikkerhetskopier av informasjon, programvare og systemer skal vedlikeholdes og testes regelmessig.',
    controlType: 'corrective',
    ciaImpact: ['availability', 'integrity'],
  },
  {
    id: 'A.8.14',
    categoryId: 'A.8',
    name: 'Redundancy of information processing facilities',
    nameNb: 'Redundans i informasjonsbehandlingsanlegg',
    description:
      'Information processing facilities shall be implemented with redundancy.',
    descriptionNb:
      'Informasjonsbehandlingsanlegg skal implementeres med redundans.',
    controlType: 'preventive',
    ciaImpact: ['availability'],
  },
  {
    id: 'A.8.15',
    categoryId: 'A.8',
    name: 'Logging',
    nameNb: 'Logging',
    description:
      'Logs that record activities, exceptions, faults and other relevant events shall be produced, stored, protected and analysed.',
    descriptionNb:
      'Logger som registrerer aktiviteter, unntak, feil og andre relevante hendelser skal produseres, lagres, beskyttes og analyseres.',
    controlType: 'detective',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },
  {
    id: 'A.8.16',
    categoryId: 'A.8',
    name: 'Monitoring activities',
    nameNb: 'Overvåkingsaktiviteter',
    description:
      'Networks, systems and applications shall be monitored for anomalous behaviour.',
    descriptionNb:
      'Nettverk, systemer og applikasjoner skal overvåkes for unormal atferd.',
    controlType: 'detective',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },
  {
    id: 'A.8.17',
    categoryId: 'A.8',
    name: 'Clock synchronization',
    nameNb: 'Klokkesynkronisering',
    description:
      'The clocks of information processing systems shall be synchronized to approved time sources.',
    descriptionNb:
      'Klokkene til informasjonsbehandlingssystemer skal synkroniseres med godkjente tidskilder.',
    controlType: 'preventive',
    ciaImpact: ['integrity'],
  },
  {
    id: 'A.8.18',
    categoryId: 'A.8',
    name: 'Use of privileged utility programs',
    nameNb: 'Bruk av privilegerte hjelpeprogrammer',
    description:
      'The use of utility programs that might be capable of overriding system and application controls shall be restricted and tightly controlled.',
    descriptionNb:
      'Bruk av hjelpeprogrammer som kan overstyre system- og applikasjonskontroller skal begrenses og kontrolleres strengt.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },
  {
    id: 'A.8.19',
    categoryId: 'A.8',
    name: 'Installation of software on operational systems',
    nameNb: 'Installasjon av programvare på operasjonelle systemer',
    description:
      'Procedures and measures shall be implemented to securely manage software installation on operational systems.',
    descriptionNb:
      'Prosedyrer og tiltak skal implementeres for å administrere programvareinstallasjon på operasjonelle systemer på en sikker måte.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },
  {
    id: 'A.8.20',
    categoryId: 'A.8',
    name: 'Networks security',
    nameNb: 'Nettverkssikkerhet',
    description:
      'Networks and network devices shall be secured, managed and controlled.',
    descriptionNb:
      'Nettverk og nettverksenheter skal sikres, administreres og kontrolleres.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },
  {
    id: 'A.8.21',
    categoryId: 'A.8',
    name: 'Security of network services',
    nameNb: 'Sikkerhet for nettverkstjenester',
    description:
      'Security mechanisms, service levels and service requirements shall be identified, implemented and monitored.',
    descriptionNb:
      'Sikkerhetsmekanismer, tjenestenivåer og tjenestekrav skal identifiseres, implementeres og overvåkes.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },
  {
    id: 'A.8.22',
    categoryId: 'A.8',
    name: 'Segregation of networks',
    nameNb: 'Segregering av nettverk',
    description:
      'Groups of information services, users and information systems shall be segregated in networks.',
    descriptionNb:
      'Grupper av informasjonstjenester, brukere og informasjonssystemer skal segregeres i nettverk.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity'],
  },
  {
    id: 'A.8.23',
    categoryId: 'A.8',
    name: 'Web filtering',
    nameNb: 'Webfiltrering',
    description:
      'Access to external websites shall be managed to reduce exposure to malicious content.',
    descriptionNb:
      'Tilgang til eksterne nettsteder skal administreres for å redusere eksponering for skadelig innhold.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity'],
  },
  {
    id: 'A.8.24',
    categoryId: 'A.8',
    name: 'Use of cryptography',
    nameNb: 'Bruk av kryptografi',
    description:
      'Rules for the effective use of cryptography shall be defined and implemented.',
    descriptionNb:
      'Regler for effektiv bruk av kryptografi skal defineres og implementeres.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity'],
  },
  {
    id: 'A.8.25',
    categoryId: 'A.8',
    name: 'Secure development life cycle',
    nameNb: 'Sikker utviklingslivssyklus',
    description:
      'Rules for the secure development of software and systems shall be established and applied.',
    descriptionNb:
      'Regler for sikker utvikling av programvare og systemer skal etableres og anvendes.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },
  {
    id: 'A.8.26',
    categoryId: 'A.8',
    name: 'Application security requirements',
    nameNb: 'Krav til applikasjonssikkerhet',
    description:
      'Information security requirements shall be identified, specified and approved.',
    descriptionNb:
      'Krav til informasjonssikkerhet skal identifiseres, spesifiseres og godkjennes.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },
  {
    id: 'A.8.28',
    categoryId: 'A.8',
    name: 'Secure coding',
    nameNb: 'Sikker koding',
    description:
      'Secure coding principles shall be applied to software development.',
    descriptionNb:
      'Sikre kodingsprinsipper skal anvendes i programvareutvikling.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },
  {
    id: 'A.8.29',
    categoryId: 'A.8',
    name: 'Security testing in development and acceptance',
    nameNb: 'Sikkerhetstesting i utvikling og akseptanse',
    description:
      'Security testing processes shall be defined and implemented in the development life cycle.',
    descriptionNb:
      'Sikkerhetstestingsprosesser skal defineres og implementeres i utviklingslivssyklusen.',
    controlType: 'detective',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },
  {
    id: 'A.8.31',
    categoryId: 'A.8',
    name: 'Separation of development, test and production environments',
    nameNb: 'Separasjon av utviklings-, test- og produksjonsmiljøer',
    description:
      'Development, testing and production environments shall be separated and secured.',
    descriptionNb:
      'Utviklings-, test- og produksjonsmiljøer skal separeres og sikres.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity'],
  },
  {
    id: 'A.8.32',
    categoryId: 'A.8',
    name: 'Change management',
    nameNb: 'Endringshåndtering',
    description:
      'Changes to information processing facilities and information systems shall be subject to change management procedures.',
    descriptionNb:
      'Endringer i informasjonsbehandlingsfasiliteter og informasjonssystemer skal underlegges prosedyrer for endringshåndtering.',
    controlType: 'preventive',
    ciaImpact: ['confidentiality', 'integrity', 'availability'],
  },
];

/**
 * Helper functions
 */
export function getIsoCategoryById(id: string): IsoControlCategory | undefined {
  return ISO_CONTROL_CATEGORIES.find(c => c.id === id);
}

export function getIsoControlById(id: string): IsoControl | undefined {
  return ISO_CONTROLS.find(c => c.id === id);
}

export function getIsoControlsByCategory(categoryId: string): IsoControl[] {
  return ISO_CONTROLS.filter(c => c.categoryId === categoryId);
}

export function getIsoControlsByCia(
  cia: 'confidentiality' | 'integrity' | 'availability'
): IsoControl[] {
  return ISO_CONTROLS.filter(c => c.ciaImpact.includes(cia));
}

export function searchIsoControls(query: string): IsoControl[] {
  const lowerQuery = query.toLowerCase();
  return ISO_CONTROLS.filter(
    c =>
      c.id.toLowerCase().includes(lowerQuery) ||
      c.name.toLowerCase().includes(lowerQuery) ||
      c.nameNb.toLowerCase().includes(lowerQuery) ||
      c.description.toLowerCase().includes(lowerQuery) ||
      c.descriptionNb.toLowerCase().includes(lowerQuery)
  );
}

/**
 * Get ISO 27001:2022 reference URL
 */
export function getIsoUrl(): string {
  return 'https://www.iso.org/standard/27001';
}
